import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { RANGE_OPTIONS } from '../ranges';
import { TARGET_PARENTS } from '../targetGroups';

// Pinned scope bar + section tabs for #/secondhand (same pattern as the CFO TARGET dashboard).
// The app routes on the hash (#/secondhand), so tabs scroll with JS instead of href="#id".

export const SH_SECTIONS: Array<[string, string]> = [
  ['secondhand-sales', '01 · Doanh số'],
  ['sh-lark', '02 · Sale hỏi Dự án'],
  ['sh-lark-team', '03 · Team Dự án'],
  ['sh-lark-detail', '04 · Chi tiết LARK'],
  ['sh-online', '05 · Hội thoại online'],
  ['secondhand-staff', '06 · Sale giới thiệu'],
  ['secondhand-demand', '07 · Nhu cầu & nguồn hàng'],
  ['secondhand-detail', '08 · Chi tiết hội thoại'],
];

const SALES_TARGETS = TARGET_PARENTS.filter(p => ['LCD', 'PCD', 'LTD', 'PED'].includes(p.code));

interface Props {
  range: string;
  onRange: (range: string) => void;
  busy: boolean;
  target: string;
  onTarget: (target: string) => void;
  canBack: boolean;
  onBack: () => void;
  canClear: boolean;
  onClear: () => void;
}

function barBottom(): number {
  return document.querySelector('.sh-pinbar')?.getBoundingClientRect().bottom ?? 0;
}

export function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  // Once pinned the bar sits at top 0, so its height (not its current bottom) is the offset to clear.
  const barHeight = (document.querySelector('.sh-pinbar') as HTMLElement | null)?.offsetHeight ?? 0;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - barHeight - 10, behavior: 'smooth' });
}

export function SecondhandPinBar({ range, onRange, busy, target, onTarget, canBack, onBack, canClear, onClear }: Props) {
  const [active, setActive] = useState(SH_SECTIONS[0][0]);
  const navLock = useRef(0);

  // Scroll spy: the last section whose top has passed under the bar is the current one.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      if (Date.now() < navLock.current) return;
      const present = SH_SECTIONS.map(([id]) => document.getElementById(id)).filter((e): e is HTMLElement => !!e);
      if (!present.length) return;
      const line = barBottom() + 48;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      const threshold = atBottom ? Math.max(line, window.innerHeight / 2) : line;
      const passed = present.filter(e => e.getBoundingClientRect().top <= threshold);
      setActive((passed[passed.length - 1] || present[0]).id);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const rangeLabel = RANGE_OPTIONS.find(o => o.v === range)?.l || range;
  const targetLabel = TARGET_PARENTS.find(p => p.code === target)?.label;

  return (
    <div className="sh-pinbar" style={{ position: 'sticky', top: 0, zIndex: 6, background: 'var(--bg)', padding: '2px 0 8px', margin: '0 0 12px' }}>
      <nav className={`ins-scopebar ${canClear ? 'is-filtered' : ''}`} aria-label="Phạm vi đang chọn">
        <div className="ins-row" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="ins-crumbs">
            <b>Hàng cũ</b>
            {targetLabel && <span>› {targetLabel}</span>}
            <em>· {rangeLabel}</em>
          </div>
          <div className="ins-actions" style={{ flexWrap: 'wrap' }}>
            <div className="ins-seg" role="group" aria-label="Kỳ">
              {RANGE_OPTIONS.map(o => (
                <button key={o.v} type="button" aria-pressed={range === o.v} disabled={busy && range !== o.v} onClick={() => onRange(o.v)}>{o.l}</button>
              ))}
            </div>
            <div className="ins-seg" role="group" aria-label="Ngành target">
              <button type="button" aria-pressed={!target} onClick={() => onTarget('')}>Tất cả</button>
              {SALES_TARGETS.map(p => (
                <button key={p.code} type="button" aria-pressed={target === p.code} title={p.label} onClick={() => onTarget(target === p.code ? '' : p.code)}>{p.label}</button>
              ))}
            </div>
            <button type="button" className="ins-iconbtn" onClick={onBack} disabled={!canBack} title="Quay lại phạm vi trước" aria-label="Quay lại"><ArrowLeft size={15} /></button>
            <button type="button" className="ins-clear" onClick={onClear} disabled={!canClear} title="Về mặc định: 28 ngày, mọi ngành"><RotateCcw size={15} /><span>Xóa lọc</span></button>
          </div>
        </div>
      </nav>
      <nav aria-label="Đi tới phần báo cáo" style={{ display: 'flex', gap: 4, overflowX: 'auto', marginTop: 6, paddingBottom: 2 }}>
        {SH_SECTIONS.map(([id, label]) => {
          const on = active === id;
          return (
            <button key={id} type="button" aria-current={on ? 'true' : undefined}
              onClick={() => { navLock.current = Date.now() + 900; setActive(id); scrollToSection(id); }}
              style={{
                whiteSpace: 'nowrap', cursor: 'pointer', borderRadius: 8, padding: '5px 11px', fontSize: 'var(--text-sm)',
                fontWeight: on ? 700 : 500, color: on ? 'var(--blue)' : 'var(--muted-strong)',
                background: on ? 'var(--paper)' : 'transparent', border: `1px solid ${on ? 'var(--soft-blue-border)' : 'transparent'}`,
                boxShadow: on ? '0 1px 2px rgba(15,23,42,.06)' : 'none',
              }}>
              {label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
